import { useEffect, useMemo, useRef, useState } from 'react'
import { ProcessEvent, TrackedProcessStatus } from '../types'
import { buildRows, formatDuration, formatOffset } from '../utils/process-debug'

/**
 * Janela de debug do processamento, fixa no canto inferior direito.
 *
 * Mostra cada etapa medida no backend (`process-tracker`): o que está rodando
 * agora e há quanto tempo, quanto levou cada uma e qual falhou. Etapa acima de
 * `SLOW_STEP_MS` fica destacada — é ali que se descobre o que está demorando.
 */

interface ProcessDebugPanelProps {
  sessionId: string | null
  status: TrackedProcessStatus | null
  startedAt: string | null
  events: ProcessEvent[]
  isConnected: boolean
  onCancel: () => Promise<void>
}

/** Etapa que passa disto ganha destaque de lenta. */
const SLOW_STEP_MS = 30_000
const MINIMIZED_KEY = 'process-debug-minimized'

function readMinimized(): boolean {
  try {
    return localStorage.getItem(MINIMIZED_KEY) === '1'
  } catch {
    return false
  }
}

function writeMinimized(value: boolean) {
  try {
    localStorage.setItem(MINIMIZED_KEY, value ? '1' : '0')
  } catch {
    // Storage bloqueado: só não lembra a preferência.
  }
}

const STATUS_LABEL: Record<TrackedProcessStatus, string> = {
  running: 'Rodando',
  completed: 'Concluído',
  failed: 'Falhou',
  cancelled: 'Cancelado',
  lost: 'Perdido',
}

export function ProcessDebugPanel({ sessionId, status, startedAt, events, isConnected, onCancel }: ProcessDebugPanelProps) {
  const [minimized, setMinimized] = useState(readMinimized)
  const [hiddenFor, setHiddenFor] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const listRef = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)

  const running = status === 'running'

  // Relógio só enquanto roda: tempo total e da etapa atual andam na tela.
  useEffect(() => {
    if (!running) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [running])

  useEffect(() => {
    if (!running) setCancelling(false)
  }, [running])

  const startedAtMs = startedAt ? Date.parse(startedAt) : now
  const rows = useMemo(() => buildRows(events, startedAtMs), [events, startedAtMs])

  // Acompanha o fim da lista, a menos que o usuário tenha rolado para cima.
  useEffect(() => {
    const el = listRef.current
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight
  }, [rows.length, minimized])

  if (!sessionId || !status || hiddenFor === sessionId) return null

  const lastEventMs = events.length > 0 ? Date.parse(events[events.length - 1].at) : startedAtMs
  const totalMs = (running ? now : lastEventMs) - startedAtMs
  const current = [...rows].reverse().find(r => r.state === 'running')
  const currentLabel = current?.label ?? [...rows].reverse().find(r => r.kind === 'progress')?.label ?? ''
  const errors = rows.filter(r => r.state === 'error').length

  const toggle = () => {
    setMinimized(m => {
      writeMinimized(!m)
      return !m
    })
  }

  const handleCancel = async () => {
    setCancelling(true)
    await onCancel()
  }

  if (minimized) {
    return (
      <button type="button" className={`debug-pill debug-${status}`} onClick={toggle} title="Abrir debug do processamento">
        {running && <span className="spinner" />}
        <span className="debug-pill-status">{STATUS_LABEL[status]}</span>
        <span className="debug-pill-label">{running ? currentLabel : `${rows.filter(r => r.kind === 'step').length} etapas`}</span>
        <span className="debug-mono">{formatDuration(totalMs)}</span>
        {errors > 0 && <span className="debug-badge-error">{errors} erro{errors > 1 ? 's' : ''}</span>}
      </button>
    )
  }

  return (
    <section className="debug-panel" aria-label="Debug do processamento">
      <header className="debug-header">
        <div className="debug-title">
          <span className={`debug-dot debug-${status}`} />
          <strong>Debug do processamento</strong>
          <span className={`debug-chip debug-${status}`}>{STATUS_LABEL[status]}</span>
        </div>
        <div className="debug-actions">
          {running && (
            <button type="button" className="btn btn-sm debug-cancel" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelando...' : 'Cancelar'}
            </button>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={toggle} title="Minimizar">
            —
          </button>
          {!running && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setHiddenFor(sessionId)} title="Fechar">
              ✕
            </button>
          )}
        </div>
      </header>

      <div className="debug-meta">
        <span>
          Total <span className="debug-mono">{formatDuration(totalMs)}</span>
        </span>
        <span>{rows.filter(r => r.kind === 'step').length} etapas</span>
        {errors > 0 && <span className="debug-text-error">{errors} com erro</span>}
        {!isConnected && <span className="debug-text-warn">socket desconectado — consultando por HTTP</span>}
      </div>

      <div
        className="debug-list"
        ref={listRef}
        onScroll={e => {
          const el = e.currentTarget
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24
        }}
      >
        {rows.length === 0 && <div className="debug-empty">Aguardando a primeira etapa...</div>}
        {rows.map(r => {
          const duration = r.state === 'running' && r.startedAtMs ? now - r.startedAtMs : r.durationMs
          const slow = r.kind === 'step' && duration !== undefined && duration >= SLOW_STEP_MS
          return (
            <div key={r.key} className={`debug-row debug-row-${r.kind} debug-row-${r.state}${slow ? ' debug-row-slow' : ''}`}>
              <span className="debug-offset debug-mono">{formatOffset(r.offsetMs)}</span>
              <span className="debug-icon" aria-hidden>
                {r.state === 'running' ? <span className="spinner" /> : r.state === 'done' ? '✓' : r.state === 'error' ? '✕' : '·'}
              </span>
              <span className="debug-label">
                {r.label}
                {r.error && <span className="debug-error-msg">{r.error}</span>}
              </span>
              {duration !== undefined && r.kind === 'step' && (
                <span className="debug-duration debug-mono">{formatDuration(duration)}</span>
              )}
            </div>
          )
        })}
      </div>
      <footer className="debug-footer debug-mono" title="ID da sessão (para procurar nos logs)">
        {sessionId}
      </footer>
    </section>
  )
}
